import { MenuItem } from "@mui/material";
import { MouseEvent, useCallback, useState } from "react";
import { ActionCell } from "../../components/ActionCell/ActionCell";
import { OneLiner } from "../../components/Table/Table";
import { useHasPermission } from "../../hooks";
import { UserPermissions } from "../../utils/permissions";
import { userFriendlyDateTimestamp } from "../../utils/timestamps";
import { TabKeyEnum, TableMenuItemInterface } from "../../utils/types";

interface Params {
  activeTab: TabKeyEnum;
  setHoveredRow: (rowIndex: number) => void;
  hoveredRow: number;
  handleEditCategory: () => void;
  handleUnpublishCategory: () => void;
  handlePublishCategory: () => void;
  handlePreviewCategory: () => void;
  handleRemoveCategory: () => void;
}

export const useGetCultureCategoriesTableColumns = (params: Params) => {
  const {
    setHoveredRow,
    hoveredRow,
    activeTab,
    handlePreviewCategory,
    handleEditCategory,
    handleUnpublishCategory,
    handlePublishCategory,
    handleRemoveCategory,
  } = params;

  const [shouldCloseMenuSelection, setShouldCloseMenuSelection] = useState<boolean>(false);

  const handlePreviewSelection = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      handlePreviewCategory();
    },
    [handlePreviewCategory],
  );

  const handleEditSelection = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      handleEditCategory();
    },
    [handleEditCategory],
  );

  const handleUnpublishSelection = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      handleUnpublishCategory();
    },
    [handleUnpublishCategory],
  );

  const handlePublishSelection = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      handlePublishCategory();
    },
    [handlePublishCategory],
  );

  const handleRemoveSelection = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      handleRemoveCategory();
    },
    [handleRemoveCategory],
  );

  const canEditCategories = useHasPermission(UserPermissions.EDIT_CULTURE_CATEGORIES);
  const canApprovePublishCategories = useHasPermission(UserPermissions.APPROVE_PUBLISH_CULTURE_CATEGORIES);
  const canUnpublishCategories = useHasPermission(UserPermissions.UNPUBLISH_CULTURE_CATEGORIES);
  const canRemoveCategories = useHasPermission(UserPermissions.REMOVE_CULTURE_CATEGORIES);

  const menuItems: TableMenuItemInterface[] = [
    {
      label: "Approve & Publish",
      onClick: handlePublishSelection,
      visible: activeTab === TabKeyEnum.ReadyForReview && canApprovePublishCategories,
    },
    {
      label: "Preview",
      onClick: handlePreviewSelection,
      visible: true,
    },
    {
      label: "Edit",
      onClick: handleEditSelection,
      visible: [TabKeyEnum.ReadyForReview, TabKeyEnum.Unpublished].includes(activeTab) && canEditCategories,
    },
    {
      label: "Unpublish",
      onClick: handleUnpublishSelection,
      visible: [TabKeyEnum.Published, TabKeyEnum.ReadyForReview].includes(activeTab) && canUnpublishCategories,
    },
    {
      label: "Remove",
      onClick: handleRemoveSelection,
      visible: [TabKeyEnum.ReadyForReview, TabKeyEnum.Unpublished].includes(activeTab) && canRemoveCategories,
      sx: { color: "red" },
    },
  ];

  const columns = [
    { accessorKey: "name", header: "Name", size: 200 },
    {
      accessorKey: "totalCultures",
      header: "Total Cultures",
      enableSorting: false,
      Cell: ({ row }: any) => <OneLiner text={String(row.original.totalCultures ?? 0)} />,
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      Cell: ({ row }: any) => userFriendlyDateTimestamp(row.original.createdAt),
    },
    {
      accessorKey: "createdBy",
      header: "Created By",
      Cell: ({ row }: any) => <OneLiner text={row.original.createdBy} />,
    },
    {
      accessorKey: "updatedAt",
      header: "Updated At",
      Cell: ({ row }: any) => userFriendlyDateTimestamp(row.original.updatedAt),
    },
    {
      accessorKey: "updatedBy",
      header: "Updated By",
      Cell: ({ row }: any) => <OneLiner text={row.original.updatedBy} />,
    },
    {
      accessorKey: "action",
      id: "action",
      size: 50,
      header: "",
      enableResizing: false,
      muiTableHeadCellProps: () => ({
        sx: {
          visibility: "hidden",
        },
      }),
      Cell: ({ row }: any) => {
        if (hoveredRow !== row.index) return null;
        return (
          <ActionCell
            shouldCloseMenuSelection={shouldCloseMenuSelection}
            setShouldCloseMenuSelection={setShouldCloseMenuSelection}
            hovered={hoveredRow === row.index}
            loading={false}
            activeTab={activeTab}
            onActionButtonClick={(event: MouseEvent<HTMLElement>) => {
              event?.stopPropagation?.();
              setHoveredRow(row.index);
            }}
          >
            {menuItems.map(
              (item, index) =>
                item.visible && (
                  <MenuItem key={index} sx={item.sx} onClick={item.onClick}>
                    {item.label}
                  </MenuItem>
                ),
            )}
          </ActionCell>
        );
      },
    },
  ];

  return columns;
};
